// ===== PageSelectionDialog.jsx - NEW =====

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Switch,
  Avatar,
  Stack,
  Divider,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { useCompletePageSelectionMutation } from '../../../redux/thunks/ai/getpages';

const PageSelectionDialog = ({ 
  open, 
  onClose, 
  sessionId, 
  availablePages,
  onSuccess 
}) => {
  const [selectedPages, setSelectedPages] = useState({});
  const [completeSelection, { isLoading }] = useCompletePageSelectionMutation();

  // Initialize selection state
  useEffect(() => {
    if (availablePages && availablePages.length > 0) {
      const initialSelection = {};
      availablePages.forEach(page => {
        initialSelection[page.id] = {
          pageId: page.id,
          connectFacebook: false,
          connectInstagram: false,
          hasInstagram: page.hasInstagram
        };
      });
      setSelectedPages(initialSelection);
    }
  }, [availablePages]);

  const handleToggleFacebook = (pageId) => {
    setSelectedPages(prev => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        connectFacebook: !prev[pageId].connectFacebook
      }
    }));
  };

  const handleToggleInstagram = (pageId) => {
    setSelectedPages(prev => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        connectInstagram: !prev[pageId].connectInstagram
      }
    }));
  };

  const handleSelectAll = () => {
    const newSelection = {};
    availablePages.forEach(page => {
      newSelection[page.id] = {
        pageId: page.id,
        connectFacebook: true,
        connectInstagram: page.hasInstagram,
        hasInstagram: page.hasInstagram
      };
    });
    setSelectedPages(newSelection);
  };

  const handleDeselectAll = () => {
    const newSelection = {};
    availablePages.forEach(page => {
      newSelection[page.id] = {
        pageId: page.id,
        connectFacebook: false,
        connectInstagram: false,
        hasInstagram: page.hasInstagram
      };
    });
    setSelectedPages(newSelection);
  };

  const handleComplete = async () => {
    try {
      // Filter only selected pages
      const selected = Object.values(selectedPages).filter(
        page => page.connectFacebook || page.connectInstagram
      );

      if (selected.length === 0) {
        alert('Please select at least one page or account to connect');
        return;
      }

      const result = await completeSelection({
        sessionId,
        selectedPages: selected
      }).unwrap();

      console.log('Page selection completed:', result);
      
       // ✅ CRITICAL FIX: Call onSuccess and let parent handle everything
        // The parent (OAuthCallback) will:
        // 1. Close the dialog (setShowPageSelection(false))
        // 2. Show success message
        // 3. Handle redirect
        if (onSuccess) {
            onSuccess(result);
        }
        
        // ❌ DO NOT call onClose() here!
        // Calling onClose triggers the cancel handler in parent
        // Let the success handler close the dialog instead
    } catch (error) {
      console.error('Failed to complete page selection:', error);
      alert('Failed to complete page selection. Please try again.');
    }
  };

  const selectedCount = Object.values(selectedPages).filter(
    page => page.connectFacebook || page.connectInstagram
  ).length;

  const facebookCount = Object.values(selectedPages).filter(
    page => page.connectFacebook
  ).length;

  const instagramCount = Object.values(selectedPages).filter(
    page => page.connectInstagram
  ).length;

  if (!availablePages || availablePages.length === 0) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              Select Pages to Connect
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {availablePages.length} page{availablePages.length !== 1 ? 's' : ''} available
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Summary Bar */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: 'primary.lighter',
            border: '1px solid',
            borderColor: 'primary.light'
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main" fontWeight="700">
                  {selectedCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Selected
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <FacebookIcon color="primary" sx={{ mb: 0.5 }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  {facebookCount} Facebook
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <InstagramIcon sx={{ color: '#E4405F', mb: 0.5 }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  {instagramCount} Instagram
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="600" gutterBottom>
            You Control What Gets Connected
            </Typography>
            <Typography variant="caption" component="div">
            Even though Facebook required you to grant permissions, <strong>you decide</strong> which 
            pages and Instagram accounts to actually connect here. Toggle Facebook and Instagram 
            independently for each page.
            </Typography>
        </Alert>

        {/* Quick Actions */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> You can connect Facebook Pages and their linked Instagram accounts. 
            Toggle each option individually or select multiple pages.
          </Typography>
        </Alert>

        {/* Page List */}
        <Stack spacing={2}>
          {availablePages.map((page) => (
            <Card 
              key={page.id}
              variant="outlined"
              sx={{
                border: (selectedPages[page.id]?.connectFacebook || selectedPages[page.id]?.connectInstagram) 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: (selectedPages[page.id]?.connectFacebook || selectedPages[page.id]?.connectInstagram)
                  ? 'primary.main'
                  : 'divider'
              }}
            >
              <CardContent>
                {/* Page Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Avatar 
                    src={page.picture} 
                    alt={page.name}
                    sx={{ width: 56, height: 56 }}
                  >
                    {page.name.charAt(0)}
                  </Avatar>
                  
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      {page.name}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Chip 
                        label={page.category} 
                        size="small" 
                        variant="outlined"
                      />
                      {page.businessName && (
                        <Chip 
                          icon={<BusinessIcon />}
                          label={page.businessName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {page.source === 'personal' && (
                        <Chip 
                          icon={<PersonIcon />}
                          label="Personal"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Connection Options */}
                <Stack spacing={1.5}>
                  {/* Facebook Toggle */}
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      bgcolor: selectedPages[page.id]?.connectFacebook ? 'primary.lighter' : 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: selectedPages[page.id]?.connectFacebook ? 'primary.light' : 'divider'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedPages[page.id]?.connectFacebook || false}
                          onChange={() => handleToggleFacebook(page.id)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FacebookIcon fontSize="small" sx={{ color: '#1877F2' }} />
                          <Typography variant="body2" fontWeight="500">
                            Connect Facebook Page
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Instagram Toggle */}
                  {page.hasInstagram ? (
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: selectedPages[page.id]?.connectInstagram ? 'rgba(228, 64, 95, 0.08)' : 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedPages[page.id]?.connectInstagram ? 'rgba(228, 64, 95, 0.3)' : 'divider'
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedPages[page.id]?.connectInstagram || false}
                            onChange={() => handleToggleInstagram(page.id)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#E4405F',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#E4405F',
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InstagramIcon fontSize="small" sx={{ color: '#E4405F' }} />
                              <Typography variant="body2" fontWeight="500">
                                Connect Instagram Business
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Avatar 
                                src={page.instagramProfilePicture} 
                                sx={{ width: 20, height: 20 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                @{page.instagramUsername}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      <Typography variant="caption">
                        No Instagram Business account linked to this page
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={selectedCount === 0 || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          {isLoading ? 'Connecting...' : `Connect ${selectedCount} Account${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageSelectionDialog;